// Parity P0 — the plain-language setup guide for "build on my own computer" (/serve). Commands sit
// in copyable blocks; every sentence around them is written for someone who has never opened a terminal.
import { Link } from '@tanstack/react-router'
import { Check, Copy, Download, Laptop, MessageCircle, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { Container, MkAnchor, MkLink, Overline, SectionHead } from '#/components/landing/mk'
import { useCopy } from '#/components/common/prompt-block'
import { m } from '#/paraglide/messages'

const NODE_URL = 'https://nodejs.org/en/download'
const CMD_CLAUDE = 'npm install -g @anthropic-ai/claude-code\nclaude'
const CMD_CODEX = 'npm install -g @openai/codex\ncodex'
const CMD_SERVE = 'npx @bystep/cli serve'
const CMD_DOCTOR = 'npx @bystep/cli serve --doctor'

function Cmd({ text, id }: { text: string; id: string }) {
  const { copied, copy } = useCopy()
  return (
    <div className="relative rounded-lg border bg-[var(--mk-bg-code,#0b0f19)] p-3 pr-11 font-mono text-[13px] leading-relaxed text-white" data-testid={`serve-cmd-${id}`}>
      <pre className="whitespace-pre-wrap break-all">{text}</pre>
      <button type="button" onClick={() => void copy(text)} aria-label="copy" className="absolute right-2 top-2 rounded-md border border-white/20 p-1.5 text-white/80 hover:bg-white/10">
        {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      </button>
    </div>
  )
}

function Step({ n, icon, title, desc, children }: { n: number; icon: React.ReactNode; title: string; desc: string; children?: React.ReactNode }) {
  return (
    <li className="flex flex-col gap-3 rounded-2xl border border-[var(--mk-border)] bg-[var(--mk-bg-elev,transparent)] p-5" data-testid={`serve-step-${n}`}>
      <div className="flex items-center gap-2">
        <span className="inline-flex size-8 items-center justify-center rounded-full bg-[var(--mk-accent)]/10 text-[var(--mk-accent)]">{icon}</span>
        <h3 className="text-[17px] font-semibold">{title}</h3>
      </div>
      <p className="text-[15px] text-[var(--mk-fg-muted)]">{desc}</p>
      {children}
    </li>
  )
}

export function ServeGuide() {
  const { copied, copy } = useCopy()
  const [agent, setAgent] = useState<'claude' | 'codex'>('claude')
  return (
    <main data-testid="serve-guide">
      <section className="pt-16 pb-10 lg:pt-24">
        <Container className="flex flex-col items-center gap-6 text-center">
          <Overline>{m.serve_page_overline()}</Overline>
          <h1 className="max-w-[22ch]">{m.serve_page_h1()}</h1>
          <p className="mk-lead max-w-[60ch]">{m.serve_page_sub()}</p>
        </Container>
      </section>

      <section className="py-8 lg:py-12">
        <Container>
          <ol className="grid gap-4 lg:grid-cols-3">
            <Step n={1} icon={<Download className="size-4" aria-hidden />} title={m.serve_step1_title()} desc={m.serve_step1_desc()}>
              <MkAnchor href={NODE_URL} variant="secondary" arrow>
                {m.serve_step1_link()}
              </MkAnchor>
            </Step>
            <Step n={2} icon={<Laptop className="size-4" aria-hidden />} title={m.serve_step2_title()} desc={m.serve_step2_desc()}>
              <div className="flex gap-1 text-[13px]">
                {(['claude', 'codex'] as const).map((a) => (
                  <button key={a} type="button" onClick={() => setAgent(a)} aria-pressed={agent === a} className={`rounded-full border px-3 py-1 ${agent === a ? 'border-[var(--mk-accent)] text-[var(--mk-accent)]' : 'text-[var(--mk-fg-muted)]'}`}>
                    {a === 'claude' ? 'Claude' : 'ChatGPT / Codex'}
                  </button>
                ))}
              </div>
              <Cmd id="agent" text={agent === 'claude' ? CMD_CLAUDE : CMD_CODEX} />
            </Step>
            <Step n={3} icon={<ShieldCheck className="size-4" aria-hidden />} title={m.serve_step3_title()} desc={m.serve_step3_desc()}>
              <Cmd id="serve" text={CMD_SERVE} />
            </Step>
          </ol>
          <div className="mt-4 rounded-2xl border border-[var(--mk-border)] p-5">
            <h3 className="text-[15px] font-semibold">{m.serve_step_check_title()}</h3>
            <p className="mb-3 text-[14px] text-[var(--mk-fg-muted)]">{m.serve_step_check_desc()}</p>
            <Cmd id="doctor" text={CMD_DOCTOR} />
          </div>
          <div className="mt-8 flex justify-center">
            <MkLink to="/sign-in" search={{ redirect: '/settings' }} arrow>
              {m.serve_page_cta()}
            </MkLink>
          </div>
        </Container>
      </section>

      <section className="py-8 lg:py-12">
        <Container className="flex flex-col gap-4">
          <SectionHead title={m.serve_ai_help_title()} lead={m.serve_ai_help_desc()} />
          <div className="relative mx-auto w-full max-w-2xl rounded-2xl border border-[var(--mk-border)] p-4 pr-12 text-[15px]" data-testid="serve-ai-prompt">
            <MessageCircle className="mb-2 size-4 text-[var(--mk-accent)]" aria-hidden />
            <p>{m.serve_ai_help_prompt()}</p>
            <button type="button" onClick={() => void copy(m.serve_ai_help_prompt())} aria-label="copy" className="absolute right-3 top-3 rounded-md border p-1.5 text-[var(--mk-fg-muted)] hover:bg-black/5">
              {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
            </button>
          </div>
        </Container>
      </section>

      <section className="py-8 pb-20 lg:py-12">
        <Container className="flex flex-col gap-6">
          <SectionHead title={m.serve_faq_title()} />
          <dl className="mx-auto grid w-full max-w-3xl gap-4 sm:grid-cols-2">
            {[
              [m.serve_faq_1_q(), m.serve_faq_1_a()],
              [m.serve_faq_2_q(), m.serve_faq_2_a()],
              [m.serve_faq_3_q(), m.serve_faq_3_a()],
              [m.serve_faq_4_q(), m.serve_faq_4_a()],
            ].map(([q, a]) => (
              <div key={q} className="rounded-2xl border border-[var(--mk-border)] p-4">
                <dt className="font-semibold">{q}</dt>
                <dd className="mt-1 text-[14px] text-[var(--mk-fg-muted)]">{a}</dd>
              </div>
            ))}
          </dl>
          <p className="text-center text-[14px] text-[var(--mk-fg-muted)]">
            <Link to="/mulai" className="underline underline-offset-2">
              ← /mulai
            </Link>
          </p>
        </Container>
      </section>
    </main>
  )
}
